// src/main.js
// ----------------------------------------------------------------------------
// Application entry point. Loads the Morphism-transformed data, mounts the
// rendered DOM, then layers interactivity on top.
//
// Boot order matters:
//   1. loadData()   → fetches JSON + runs Morphism schemas (view-models)
//   2. renderApp()  → injects markup (`.scroll` elements, lazy images, ids)
//   3. initReveal() → plants Motion UI hidden state + lazy-imports scroll.js
//                      (jQuery must already be global — that's why scroll-reveal
//                      imports jquery and assigns window.$ *before* its import)
//   4. bootUI()     → Typed, theme, nav, scroll-spy, back-to-top, canvas, form
// ----------------------------------------------------------------------------
import MotionUI from './lib/motion.js';
import './styles/app.css';
import { loadData } from './data.js';
import { renderApp } from './render.js';
import { initAnimations, initThreeJSHero } from './lib/animations.js';
import { bootUI } from './ui.js';

// Hydration guard: if the page is served without a <div id="app">, bail.
function mount() {
  const app = document.getElementById('app');
  if (!app) {
    console.warn('CV: #app root missing; aborting bootstrap.');
    return;
  }

  loadData()
    .then((data) => {
      // 1+2. Render first so nodes and interactive hooks exist.
      renderApp(data);

      // 3. Reveal engine & Smooth Scroll (Lenis + GSAP).
      initAnimations();
      
      // 4. ThreeJS Hero
      initThreeJSHero();

      // 5. Interactivity (does not depend on scroll.js having loaded).
      bootUI(data, MotionUI);
    })
    .catch((err) => {
      console.error(err);
      app.innerHTML = `
        <div class="container" style="padding:3rem 0;text-align:center">
          <p class="muted">Something went wrong while loading the CV data.</p>
          <p class="muted small">Check the console for details, or <a href="javascript:location.reload()">retry</a>.</p>
        </div>`;
    });
}

// Ensure the DOM is ready (index.html uses a module script = deferred by
// default, but be defensive).
if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}
